import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowUp, ArrowDown, FloppyDisk, ArrowCounterClockwise, Storefront, Warning, UserList, TelegramLogo, DownloadSimple, UploadSimple, ShieldWarning } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ExpenseConfigCard } from '@/components/settings/ExpenseConfigCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function HdMigrationTool() {
  const [pending, setPending] = useState(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ processed: 0, primary: 0, extras: 0, errors: [] });
  const [initialTotal, setInitialTotal] = useState(0);

  const checkStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/migrate-hd-status`, { withCredentials: true });
      setPending(data.pending);
      return data.pending;
    } catch (e) {
      toast.error('No se pudo consultar el estado');
      return null;
    }
  };

  useEffect(() => { checkStatus(); }, []);

  const run = async () => {
    setRunning(true);
    setProgress({ processed: 0, primary: 0, extras: 0, errors: [] });
    let total = pending;
    if (total == null) total = await checkStatus();
    if (!total || total === 0) { setRunning(false); return; }
    setInitialTotal(total);
    let stopping = false;
    // Loop hasta que pending llegue a 0
    while (!stopping) {
      try {
        const { data } = await axios.post(`${API}/admin/migrate-hd-batch?limit=3`, {}, { withCredentials: true });
        setProgress(prev => ({
          processed: prev.processed + (data.processed || 0),
          primary: prev.primary + (data.primary_moved || 0),
          extras: prev.extras + (data.extras_moved || 0),
          errors: [...prev.errors, ...(data.errors || [])].slice(0, 20),
        }));
        setPending(data.pending);
        if (data.pending === 0) stopping = true;
        if (data.processed === 0 && (data.errors || []).length > 0) {
          // Nada procesado y hay errores → detener para evitar loop infinito
          stopping = true;
        }
      } catch (e) {
        toast.error('Error durante la migración');
        stopping = true;
      }
      // Pequeña pausa para no saturar
      await new Promise(r => setTimeout(r, 200));
    }
    setRunning(false);
    toast.success('Migración terminada');
  };

  return (
    <div>
      {pending === null ? (
        <p className="text-xs text-slate-500">Consultando estado…</p>
      ) : pending === 0 && !running ? (
        <p className="text-xs text-emerald-700">✅ Todas las imágenes HD ya están en el Object Store.</p>
      ) : (
        <>
          <p className="text-xs text-slate-700 mb-2">
            <b>{pending}</b> producto{pending === 1 ? '' : 's'} con imágenes HD en base64 pendientes de migrar.
          </p>
          <Button
            onClick={run}
            disabled={running || pending === 0}
            size="sm"
            data-testid="run-hd-migration"
          >
            {running ? `Migrando… (${progress.processed}/${initialTotal})` : `Migrar ${pending}`}
          </Button>
        </>
      )}
      {(progress.processed > 0 || progress.primary > 0 || progress.extras > 0) && (
        <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded text-xs space-y-1">
          <div>Procesados: <b>{progress.processed}</b></div>
          <div>Portadas movidas: <b>{progress.primary}</b></div>
          <div>Imágenes extras movidas: <b>{progress.extras}</b></div>
          {progress.errors.length > 0 && (
            <div className="text-rose-700 mt-1">
              Errores: {progress.errors.length}
              <ul className="ml-3 mt-1">
                {progress.errors.slice(0, 5).map((e, i) => (
                  <li key={i}>· {e.id?.slice(0, 8)} — {e.msg?.slice(0, 60)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


const DEFAULT_ITEMS = [
  { path: '/dashboard', label: 'Dashboard' },
  { path: '/pos', label: 'Punto de Venta' },
  { path: '/products', label: 'Productos' },
  { path: '/packages', label: 'Paquetes' },
  { path: '/categories', label: 'Categorías' },
  { path: '/clients', label: 'Clientes' },
  { path: '/sellers', label: 'Vendedores' },
  { path: '/shipments', label: 'Envíos' },
  { path: '/apartados', label: 'Apartados' },
  { path: '/collect-shipments', label: 'Cobrar Envíos' },
  { path: '/shipping-costs', label: 'Costos de Envío' },
  { path: '/mercado-libre', label: 'Mercado Libre' },
  { path: '/inventory', label: 'Inventario' },
  { path: '/purchases', label: 'Compras' },
  { path: '/exits', label: 'Salidas' },
  { path: '/commissions', label: 'Comisiones' },
  { path: '/catalog', label: 'Catálogo' },
  { path: '/cuts', label: 'Corte de Caja' },
  { path: '/reports', label: 'Reportes' },
  { path: '/sales-report', label: 'Reporte Ventas' },
];

const SEASONAL_THEME_META = {
  halloween:   { label: 'Halloween',     icon: '🎃', hint: 'Calabazas, murciélagos y naranja/negro' },
  christmas:   { label: 'Navidad',       icon: '🎄', hint: 'Árboles, nieve y rojo/verde' },
  day_of_dead: { label: 'Día de Muertos', icon: '💀', hint: 'Catrinas, cempasúchil y velas' },
  mothers_day: { label: 'Día de la Madre', icon: '💐', hint: 'Flores, corazones y rosa' },
  valentines:  { label: 'San Valentín',   icon: '💘', hint: 'Corazones, rosas y rojo intenso' },
  kings:       { label: 'Día de Reyes',   icon: '👑', hint: 'Coronas, estrellas y dorado' },
};
const SEASONAL_THEME_ORDER = ['halloween', 'day_of_dead', 'kings', 'valentines', 'mothers_day', 'christmas'];

export const Settings = () => {
  const [items, setItems] = useState(DEFAULT_ITEMS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [branchName, setBranchName] = useState('');
  const [savingBranch, setSavingBranch] = useState(false);
  const [allowNegativeStock, setAllowNegativeStock] = useState(false);
  const [savingNegStock, setSavingNegStock] = useState(false);
  const [allowNegativeTransfers, setAllowNegativeTransfers] = useState(false);
  const [savingNegTransfers, setSavingNegTransfers] = useState(false);
  const [allowNegativeExits, setAllowNegativeExits] = useState(false);
  const [savingNegExits, setSavingNegExits] = useState(false);
  const [allowManualSeller, setAllowManualSeller] = useState(false);
  const [savingManualSeller, setSavingManualSeller] = useState(false);
  const [seasonalTheme, setSeasonalTheme] = useState(null); // 'halloween' | 'christmas' | ... | null
  const [savingSeasonalTheme, setSavingSeasonalTheme] = useState(false);
  const [salesConfirmByBranch, setSalesConfirmByBranch] = useState({}); // { branch_id: bool }
  const [salesConfirmBranchNames, setSalesConfirmBranchNames] = useState({});
  const [savingSalesConfirm, setSavingSalesConfirm] = useState({});
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  const [restoreDialog, setRestoreDialog] = useState({ open: false, file: null, confirm: '', restoring: false, result: null });
  const [autoBackups, setAutoBackups] = useState([]);
  const [autoBackupsLoading, setAutoBackupsLoading] = useState(false);
  const [runNowLoading, setRunNowLoading] = useState(false);
  const [variantExtras, setVariantExtras] = useState({ loading: false, data: null, cleaning: false });

  const scanVariantExtras = async () => {
    setVariantExtras({ loading: true, data: null, cleaning: false });
    try {
      const { data } = await axios.get(`${API}/admin/products/variants-with-extras`, { withCredentials: true });
      setVariantExtras({ loading: false, data, cleaning: false });
      if (data.count === 0) toast.success('No hay variantes con imágenes extra pegadas');
    } catch (e) {
      setVariantExtras({ loading: false, data: null, cleaning: false });
      toast.error(e?.response?.data?.detail || 'Error al escanear');
    }
  };

  const cleanVariantExtras = async () => {
    if (!variantExtras.data || variantExtras.data.count === 0) return;
    if (!window.confirm(`¿Vaciar "extra_images" de ${variantExtras.data.count} variantes? Esta acción no se puede deshacer.`)) return;
    setVariantExtras((s) => ({ ...s, cleaning: true }));
    try {
      const { data } = await axios.post(`${API}/admin/products/variants-clear-extras`, { confirm: true }, { withCredentials: true });
      toast.success(`Limpiadas: ${data.modified} variantes`);
      await scanVariantExtras();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al limpiar');
      setVariantExtras((s) => ({ ...s, cleaning: false }));
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/config/menu-order`, { withCredentials: true });
        const order = data.order || [];
        if (order.length > 0) {
          const byPath = Object.fromEntries(DEFAULT_ITEMS.map(i => [i.path, i]));
          const ordered = order.map(p => byPath[p]).filter(Boolean);
          const remaining = DEFAULT_ITEMS.filter(i => !order.includes(i.path));
          setItems([...ordered, ...remaining]);
        }
      } catch (e) {
        // sin orden guardado o no admin
      }
      try {
        const { data } = await axios.get(`${API}/config/branch-name`, { withCredentials: true });
        setBranchName(data.name || '');
      } catch (e) {
        // ignore
      }
      try {
        const { data } = await axios.get(`${API}/config/allow-negative-stock`, { withCredentials: true });
        setAllowNegativeStock(!!data.enabled);
      } catch (e) {
        // ignore
      }
      try {
        const { data } = await axios.get(`${API}/config/allow-negative-stock-transfers`, { withCredentials: true });
        setAllowNegativeTransfers(!!data.enabled);
      } catch (e) {
        // ignore
      }
      try {
        const { data } = await axios.get(`${API}/config/allow-negative-stock-exits`, { withCredentials: true });
        setAllowNegativeExits(!!data.enabled);
      } catch (e) {
        // ignore
      }
      try {
        const { data } = await axios.get(`${API}/config/allow-manual-seller`, { withCredentials: true });
        setAllowManualSeller(!!data.enabled);
      } catch (e) {
        // ignore
      }
      try {
        const { data } = await axios.get(`${API}/config/seasonal-theme`, { withCredentials: true });
        setSeasonalTheme(data?.active || null);
      } catch (e) {
        // ignore
      }
      try {
        const { data } = await axios.get(`${API}/config/sales-confirm`, { withCredentials: true });
        setSalesConfirmByBranch(data?.branches || {});
        setSalesConfirmBranchNames(data?.names || {});
      } catch (e) {
        // ignore
      }
      try {
        const { data } = await axios.get(`${API}/admin/backup/auto/list`, { withCredentials: true });
        setAutoBackups(data.items || []);
      } catch (e) {
        // ignore (no admin)
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const saveBranchName = async () => {
    setSavingBranch(true);
    try {
      await axios.put(`${API}/config/branch-name`, { name: branchName }, { withCredentials: true });
      toast.success('Nombre de la sucursal actualizado');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingBranch(false);
    }
  };

  const toggleNegativeStock = async (checked) => {
    setSavingNegStock(true);
    const prev = allowNegativeStock;
    setAllowNegativeStock(checked);
    try {
      await axios.put(`${API}/config/allow-negative-stock`, { enabled: checked }, { withCredentials: true });
      toast.success(checked ? 'Venta sin existencias HABILITADA' : 'Venta sin existencias deshabilitada');
    } catch (e) {
      setAllowNegativeStock(prev);
      toast.error(e?.response?.data?.detail || 'Solo admin puede cambiar esto');
    } finally {
      setSavingNegStock(false);
    }
  };

  const toggleNegativeTransfers = async (checked) => {
    setSavingNegTransfers(true);
    const prev = allowNegativeTransfers;
    setAllowNegativeTransfers(checked);
    try {
      await axios.put(`${API}/config/allow-negative-stock-transfers`, { enabled: checked }, { withCredentials: true });
      toast.success(checked ? 'Transferencias sin stock HABILITADAS' : 'Transferencias sin stock deshabilitadas');
    } catch (e) {
      setAllowNegativeTransfers(prev);
      toast.error(e?.response?.data?.detail || 'Solo admin puede cambiar esto');
    } finally {
      setSavingNegTransfers(false);
    }
  };

  const toggleNegativeExits = async (checked) => {
    setSavingNegExits(true);
    const prev = allowNegativeExits;
    setAllowNegativeExits(checked);
    try {
      await axios.put(`${API}/config/allow-negative-stock-exits`, { enabled: checked }, { withCredentials: true });
      toast.success(checked ? 'Salidas sin stock HABILITADAS' : 'Salidas sin stock deshabilitadas');
    } catch (e) {
      setAllowNegativeExits(prev);
      toast.error(e?.response?.data?.detail || 'Solo admin puede cambiar esto');
    } finally {
      setSavingNegExits(false);
    }
  };

  const toggleManualSeller = async (checked) => {
    setSavingManualSeller(true);
    const prev = allowManualSeller;
    setAllowManualSeller(checked);
    try {
      await axios.put(`${API}/config/allow-manual-seller`, { enabled: checked }, { withCredentials: true });
      toast.success(checked
        ? 'Asignación manual de vendedor HABILITADA (sin QR)'
        : 'Asignación manual deshabilitada — vuelve a requerir QR');
    } catch (e) {
      setAllowManualSeller(prev);
      toast.error(e?.response?.data?.detail || 'Solo admin puede cambiar esto');
    } finally {
      setSavingManualSeller(false);
    }
  };

  const changeSeasonalTheme = async (nextTheme) => {
    // nextTheme = null para desactivar
    setSavingSeasonalTheme(true);
    const prev = seasonalTheme;
    setSeasonalTheme(nextTheme);
    try {
      await axios.put(`${API}/config/seasonal-theme`, { theme: nextTheme }, { withCredentials: true });
      toast.success(nextTheme ? `Tema activado: ${SEASONAL_THEME_META[nextTheme]?.label || nextTheme}` : 'Tema estacional desactivado');
    } catch (e) {
      setSeasonalTheme(prev);
      toast.error(e?.response?.data?.detail || 'Solo admin puede cambiar el tema');
    } finally {
      setSavingSeasonalTheme(false);
    }
  };

  const toggleSalesConfirmBranch = async (branchId, checked) => {
    setSavingSalesConfirm(s => ({ ...s, [branchId]: true }));
    const prev = salesConfirmByBranch[branchId];
    setSalesConfirmByBranch(s => ({ ...s, [branchId]: checked }));
    try {
      await axios.put(`${API}/config/sales-confirm`, { branch_id: branchId, enabled: checked }, { withCredentials: true });
      toast.success(checked
        ? `Verificación activada en ${salesConfirmBranchNames[branchId] || 'sucursal'}`
        : `Verificación desactivada en ${salesConfirmBranchNames[branchId] || 'sucursal'}`);
    } catch (e) {
      setSalesConfirmByBranch(s => ({ ...s, [branchId]: prev }));
      toast.error(e?.response?.data?.detail || 'No se pudo cambiar');
    } finally {
      setSavingSalesConfirm(s => ({ ...s, [branchId]: false }));
    }
  };

  const downloadBackup = async () => {
    setDownloadingBackup(true);
    try {
      const resp = await axios.get(`${API}/admin/backup/export`, {
        withCredentials: true,
        responseType: 'blob',
      });
      // Try to read filename from Content-Disposition
      const cd = resp.headers?.['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/i);
      const filename = m ? m[1] : `backup_${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('✅ Respaldo descargado. Guárdalo en un lugar seguro.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al generar el respaldo');
    } finally {
      setDownloadingBackup(false);
    }
  };

  const submitRestore = async () => {
    if (!restoreDialog.file) { toast.error('Selecciona el archivo .json'); return; }
    if (restoreDialog.confirm !== 'RESTAURAR') { toast.error('Escribe RESTAURAR para confirmar'); return; }
    setRestoreDialog(s => ({ ...s, restoring: true, result: null }));
    try {
      const fd = new FormData();
      fd.append('file', restoreDialog.file);
      const { data } = await axios.post(
        `${API}/admin/backup/restore?confirm=RESTAURAR`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setRestoreDialog(s => ({ ...s, restoring: false, result: data }));
      toast.success('✅ Restauración completada. Recarga la página.');
    } catch (e) {
      setRestoreDialog(s => ({ ...s, restoring: false }));
      toast.error(e?.response?.data?.detail || 'Error al restaurar');
    }
  };

  const refreshAutoBackups = async () => {
    setAutoBackupsLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/backup/auto/list`, { withCredentials: true });
      setAutoBackups(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al listar respaldos');
    } finally {
      setAutoBackupsLoading(false);
    }
  };

  const runBackupNow = async () => {
    setRunNowLoading(true);
    try {
      await axios.post(`${API}/admin/backup/auto/run-now`, {}, { withCredentials: true });
      toast.success('✅ Respaldo generado en disco');
      await refreshAutoBackups();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al generar respaldo');
    } finally {
      setRunNowLoading(false);
    }
  };

  const downloadAutoBackup = async (filename) => {
    try {
      const resp = await axios.get(`${API}/admin/backup/auto/download/${encodeURIComponent(filename)}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al descargar');
    }
  };

  const deleteAutoBackup = async (filename) => {
    if (!window.confirm(`¿Borrar ${filename}? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/admin/backup/auto/${encodeURIComponent(filename)}`, { withCredentials: true });
      toast.success('Respaldo borrado');
      await refreshAutoBackups();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al borrar');
    }
  };

  const move = (idx, dir) => {
    const newItems = [...items];
    const target = idx + dir;
    if (target < 0 || target >= newItems.length) return;
    [newItems[idx], newItems[target]] = [newItems[target], newItems[idx]];
    setItems(newItems);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/config/menu-order`,
        { order: items.map(i => i.path) },
        { withCredentials: true });
      toast.success('Orden del menú guardado. Recarga la página para ver el cambio.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setItems(DEFAULT_ITEMS);
    toast.info('Orden restablecido. No olvides guardar.');
  };

  // Drag & drop nativo HTML5
  const onDragStart = (e, idx) => e.dataTransfer.setData('text/plain', String(idx));
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (e, targetIdx) => {
    e.preventDefault();
    const fromIdx = parseInt(e.dataTransfer.getData('text/plain'));
    if (Number.isNaN(fromIdx) || fromIdx === targetIdx) return;
    const newItems = [...items];
    const [moved] = newItems.splice(fromIdx, 1);
    newItems.splice(targetIdx, 0, moved);
    setItems(newItems);
  };

  if (loading) return <MainLayout><p className="p-8">Cargando…</p></MainLayout>;

  return (
    <MainLayout>
      <div className="container max-w-3xl py-6 space-y-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Configuración</h1>
          <p className="text-muted-foreground">Personaliza el orden del menú lateral y los datos de tu sucursal.</p>
        </div>

        <ExpenseConfigCard />

        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Storefront size={22} weight="bold" />
              Sucursal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="branch-name">Nombre de la sucursal</Label>
              <p className="text-xs text-muted-foreground">
                Aparecerá al pie de cada notificación de Telegram (ventas, envíos, aprobaciones, alertas, etc.).
              </p>
              <div className="flex gap-2">
                <Input
                  id="branch-name"
                  value={branchName}
                  onChange={(e) => setBranchName(e.target.value)}
                  placeholder="Ej: Sucursal Centro"
                  maxLength={80}
                  data-testid="branch-name-input"
                />
                <Button onClick={saveBranchName} disabled={savingBranch} data-testid="save-branch-name" className="bg-violet-600 hover:bg-violet-700">
                  <FloppyDisk size={16} className="mr-1" /> {savingBranch ? 'Guardando…' : 'Guardar'}
                </Button>
              </div>
              {branchName && (
                <div className="bg-slate-50 border border-border rounded p-2 mt-2">
                  <p className="text-xs text-slate-500 mb-1">Vista previa del pie:</p>
                  <p className="text-sm italic">— 📍 {branchName}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Temas estacionales */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="text-xl leading-none">🎉</span>
              Temas estacionales
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Activa un tema para decorar todas las páginas públicas (ofertas, catálogo, mayorista, vendedor, cotizador, entregador) con un
              banner animado y lluvia de emojis. <b>Se aplica sin importar la fecha</b> — tú decides cuándo encenderlo o apagarlo.
              Los productos coreanos (<code className="bg-slate-100 px-1 rounded">/coreanos</code>) nunca llevan decoración.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {SEASONAL_THEME_ORDER.map((key) => {
                const meta = SEASONAL_THEME_META[key];
                const active = seasonalTheme === key;
                return (
                  <button
                    key={key}
                    onClick={() => changeSeasonalTheme(active ? null : key)}
                    disabled={savingSeasonalTheme}
                    className={`relative text-left rounded-xl border-2 p-3 transition active:scale-95 disabled:opacity-60 ${
                      active
                        ? 'border-emerald-500 bg-emerald-50 shadow-sm'
                        : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid={`seasonal-theme-btn-${key}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-2xl leading-none">{meta.icon}</span>
                      <span className={`font-semibold text-sm ${active ? 'text-emerald-800' : 'text-slate-800'}`}>{meta.label}</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1 leading-snug">{meta.hint}</p>
                    {active && (
                      <span className="absolute top-1.5 right-1.5 text-[10px] font-bold text-emerald-700 bg-emerald-100 border border-emerald-300 rounded-full px-2 py-0.5">
                        ACTIVO
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <div className="text-xs text-muted-foreground">
                {seasonalTheme
                  ? <>Tema activo: <b className="text-slate-800">{SEASONAL_THEME_META[seasonalTheme]?.icon} {SEASONAL_THEME_META[seasonalTheme]?.label}</b></>
                  : <>Ningún tema activo.</>}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => changeSeasonalTheme(null)}
                disabled={savingSeasonalTheme || !seasonalTheme}
                data-testid="seasonal-theme-clear"
              >
                Desactivar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Verificación de operaciones cada 2 horas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="text-xl leading-none">🧐</span>
              Verificación cada 2 horas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Cuando está <b>activa</b> en una sucursal, el POS muestra un pop-up a las 2 horas de la primera venta del día
              pidiendo escribir una palabra aleatoria para confirmar que no quedan entradas, salidas o transferencias
              pendientes de registrar. También <b>al cerrar el corte</b>. Se puede omitir una sola vez por día (15&nbsp;min
              de margen); después es obligatorio.
            </p>
            <div className="space-y-2">
              {Object.entries(salesConfirmBranchNames).map(([bid, name]) => {
                const enabled = !!salesConfirmByBranch[bid];
                const saving = !!savingSalesConfirm[bid];
                return (
                  <label
                    key={bid}
                    className={`flex items-center justify-between gap-3 p-3 rounded-xl border-2 cursor-pointer select-none ${enabled ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                    data-testid={`sales-confirm-toggle-${bid}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xl">🏬</span>
                      <div>
                        <p className={`font-semibold text-sm ${enabled ? 'text-emerald-800' : 'text-slate-700'}`}>{name}</p>
                        <p className="text-[11px] text-slate-500">{enabled ? 'Activa' : 'Desactivada'}</p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={enabled}
                      disabled={saving}
                      onChange={(e) => toggleSalesConfirmBranch(bid, e.target.checked)}
                      className="h-5 w-5 cursor-pointer accent-emerald-600"
                    />
                  </label>
                );
              })}
              {Object.keys(salesConfirmBranchNames).length === 0 && (
                <p className="text-xs text-slate-400">Cargando sucursales…</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Herramientas de mantenimiento */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Warning size={22} weight="bold" className="text-sky-600" />
              Mantenimiento
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm font-semibold">Fotos “pegadas” en variantes</p>
              <p className="text-xs text-muted-foreground mt-0.5 mb-2">
                Algunas variantes pueden tener imágenes adicionales heredadas de versiones anteriores del sistema.
                Esto hace que aparezcan flechas de carrusel en catálogo/ofertas con fotos que no corresponden.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={scanVariantExtras}
                  disabled={variantExtras.loading || variantExtras.cleaning}
                  data-testid="scan-variant-extras"
                >
                  {variantExtras.loading ? 'Escaneando…' : 'Escanear variantes'}
                </Button>
                {variantExtras.data && variantExtras.data.count > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={cleanVariantExtras}
                    disabled={variantExtras.cleaning}
                    data-testid="clean-variant-extras"
                  >
                    {variantExtras.cleaning ? 'Limpiando…' : `Limpiar ${variantExtras.data.count} variantes`}
                  </Button>
                )}
              </div>
              {variantExtras.data && variantExtras.data.count > 0 && (
                <div className="mt-3 border border-amber-200 bg-amber-50 rounded p-3 max-h-64 overflow-y-auto">
                  <p className="text-xs font-semibold text-amber-800 mb-2">
                    {variantExtras.data.count} variantes con imágenes extra:
                  </p>
                  <ul className="text-xs space-y-1 text-slate-700">
                    {variantExtras.data.variants.slice(0, 30).map((v) => (
                      <li key={v.id} className="flex justify-between gap-2">
                        <span className="truncate">
                          <b>{v.parent_name || 'Sin padre'}</b> · {v.variant_name || v.sku}
                        </span>
                        <span className="text-amber-700 font-mono flex-shrink-0">{v.extra_images_count} extras</span>
                      </li>
                    ))}
                    {variantExtras.data.variants.length > 30 && (
                      <li className="text-slate-500 italic">…y {variantExtras.data.variants.length - 30} más</li>
                    )}
                  </ul>
                </div>
              )}
              {variantExtras.data && variantExtras.data.count === 0 && (
                <p className="text-xs text-emerald-700 mt-2">✅ Ninguna variante tiene imágenes extra pegadas.</p>
              )}
            </div>

            <div className="pt-4 border-t border-slate-200">
              <p className="text-sm font-semibold">Migrar imágenes HD al Object Store</p>
              <p className="text-xs text-muted-foreground mt-0.5 mb-2">
                Mueve las imágenes HD que están guardadas como base64 en la base de datos al Object Store (mejora el rendimiento).
                Puedes correrlo en cualquier momento — es seguro y se puede parar y reanudar.
              </p>
              <HdMigrationTool />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Warning size={22} weight="bold" className="text-amber-600" />
              Inventario
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label htmlFor="allow-neg-stock" className="text-base font-medium">
                  Permitir vender sin existencias
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Cuando está activo, el POS permite agregar y vender productos aun con stock 0
                  o negativo. Útil cuando el inventario físico no coincide con el sistema.
                  El stock quedará en números negativos hasta que se ajuste.
                </p>
                <p className="text-xs text-amber-700 mt-1 font-medium">
                  Solo administradores pueden cambiar esta opción.
                </p>
              </div>
              <Switch
                id="allow-neg-stock"
                checked={allowNegativeStock}
                disabled={savingNegStock}
                onCheckedChange={toggleNegativeStock}
                data-testid="allow-negative-stock-toggle"
              />
            </div>

            <div className="border-t border-slate-200 mt-4 pt-4 flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label htmlFor="allow-neg-transfers" className="text-base font-medium">
                  Permitir transferir sin existencias
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Cuando está activo, se pueden crear <b>transferencias entre sucursales</b>
                  aunque la sucursal origen no tenga stock suficiente. El inventario de la
                  sucursal origen quedará en negativo hasta que se ajuste. Independiente del
                  switch de arriba.
                </p>
                <p className="text-xs text-amber-700 mt-1 font-medium">
                  Solo administradores pueden cambiar esta opción.
                </p>
              </div>
              <Switch
                id="allow-neg-transfers"
                checked={allowNegativeTransfers}
                disabled={savingNegTransfers}
                onCheckedChange={toggleNegativeTransfers}
                data-testid="allow-negative-transfers-toggle"
              />
            </div>

            <div className="flex items-start justify-between gap-4 pt-4 border-t mt-4">
              <div className="flex-1">
                <Label htmlFor="allow-neg-exits" className="text-base font-medium">
                  Permitir salidas sin existencias
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Cuando está activo, se pueden registrar <b>salidas de stock</b>
                  (mermas, regalos, uso interno, etc.) aunque el producto no tenga
                  existencia suficiente. El inventario quedará en negativo hasta
                  que se ajuste. Independiente de los switches de arriba.
                </p>
                <p className="text-xs text-amber-700 mt-1 font-medium">
                  Solo administradores pueden cambiar esta opción.
                </p>
              </div>
              <Switch
                id="allow-neg-exits"
                checked={allowNegativeExits}
                disabled={savingNegExits}
                onCheckedChange={toggleNegativeExits}
                data-testid="allow-negative-exits-toggle"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <UserList size={22} weight="bold" className="text-violet-600" />
              Punto de Venta — Vendedor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label htmlFor="allow-manual-seller" className="text-base font-medium">
                  Permitir asignar vendedor manualmente (sin QR)
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Cuando está activo, en el POS se puede seleccionar al vendedor desde un menú
                  directamente, sin necesidad de escanear el código QR ni capturar contraseña.
                  No se enviará la notificación de "QR escaneado" al vendedor — solo recibirá
                  la notificación normal cuando la venta se concrete.
                </p>
                <p className="text-xs text-amber-700 mt-1 font-medium">
                  Cuando esté apagado, vuelve la forma de trabajo actual: el vendedor se asigna
                  únicamente al escanear su QR (o vía Ctrl+Shift+V con contraseña de admin).
                  Solo administradores pueden cambiar esta opción.
                </p>
              </div>
              <Switch
                id="allow-manual-seller"
                checked={allowManualSeller}
                disabled={savingManualSeller}
                onCheckedChange={toggleManualSeller}
                data-testid="allow-manual-seller-toggle"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TelegramLogo size={22} weight="fill" className="text-sky-600" />
              Comandos del Bot de Telegram
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground mb-3">
              Estos comandos están disponibles en el bot. Escríbelos en el chat con el bot.
            </p>
            <div className="space-y-3 text-sm">
              <div>
                <p className="font-semibold text-slate-800 mb-1">🔎 Consulta de productos</p>
                <ul className="space-y-1 text-xs text-slate-700">
                  <li><code className="bg-slate-100 px-1.5 py-0.5 rounded">/buscar TEXTO</code> — busca producto por nombre, variante o SKU</li>
                  <li><code className="bg-slate-100 px-1.5 py-0.5 rounded">/precio TEXTO</code> — muestra todos los precios (1–5, mayoreo, medio mayoreo) y stock</li>
                  <li><code className="bg-slate-100 px-1.5 py-0.5 rounded">/stock TEXTO</code> — alias de búsqueda con stock disponible</li>
                  <li><code className="bg-slate-100 px-1.5 py-0.5 rounded">/promos</code> — lista las promociones activas (con foto)</li>
                  <li>También puedes escribir directamente el nombre <em>sin comando</em>.</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-slate-800 mb-1">🚚 Envíos</p>
                <ul className="space-y-1 text-xs text-slate-700">
                  <li><code className="bg-slate-100 px-1.5 py-0.5 rounded">/costo COLONIA</code> — cotiza costo de envío a una colonia (búsqueda aproximada)</li>
                  <li><code className="bg-slate-100 px-1.5 py-0.5 rounded">/cobranza</code> — resumen de envíos pendientes para entregadores</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-slate-800 mb-1">💰 Vendedores</p>
                <ul className="space-y-1 text-xs text-slate-700">
                  <li><code className="bg-slate-100 px-1.5 py-0.5 rounded">/comisiones</code> — resumen de ventas y comisiones</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-slate-800 mb-1">⚙️ Cuenta</p>
                <ul className="space-y-1 text-xs text-slate-700">
                  <li><code className="bg-slate-100 px-1.5 py-0.5 rounded">/start</code> — bienvenida (también acepta deep-link: <code>/start CODIGO</code>)</li>
                  <li><code className="bg-slate-100 px-1.5 py-0.5 rounded">/help</code> o <code className="bg-slate-100 px-1.5 py-0.5 rounded">/ayuda</code> — muestra todos los comandos</li>
                  <li><code className="bg-slate-100 px-1.5 py-0.5 rounded">/vincular CODIGO</code> — vincula tu cuenta (vendedor / entregador)</li>
                  <li><code className="bg-slate-100 px-1.5 py-0.5 rounded">/desvincular</code> — desvincula tu cuenta</li>
                  <li><code className="bg-slate-100 px-1.5 py-0.5 rounded">/yo</code> — muestra tu chat_id (útil para soporte)</li>
                </ul>
              </div>
              <div className="bg-sky-50 border border-sky-200 rounded p-2 text-xs text-sky-900">
                💡 <b>Tip:</b> Las aprobaciones (descuentos, cancelaciones, edición post-venta, ML, etc.)
                se manejan automáticamente con botones <b>Aprobar / Rechazar</b> en el grupo de admins.
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <ShieldWarning size={22} weight="bold" className="text-rose-600" />
              Respaldo de la Base de Datos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium">📥 Descargar respaldo</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Genera un archivo <code>.json</code> con TODAS tus colecciones (productos, ventas,
                  comisiones, cortes, clientes, vendedores, etc.). Guárdalo en un lugar seguro —
                  podrás usarlo más adelante para restaurar si pasa algo.
                </p>
                <Button
                  onClick={downloadBackup}
                  disabled={downloadingBackup}
                  className="mt-2 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="backup-download-btn"
                >
                  <DownloadSimple size={18} weight="bold" className="mr-2" />
                  {downloadingBackup ? 'Generando…' : 'Descargar respaldo (.json)'}
                </Button>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm font-medium">📤 Restaurar desde archivo</p>
                <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 mt-1">
                  ⚠️ <b>DESTRUCTIVO</b>: reemplaza el contenido de tus colecciones por el del archivo.
                  Solo usa esta opción si tienes plena seguridad de lo que vas a restaurar.
                  Te recomendamos descargar un respaldo del estado actual ANTES de restaurar.
                </p>
                <Button
                  onClick={() => setRestoreDialog({ open: true, file: null, confirm: '', restoring: false, result: null })}
                  variant="outline"
                  className="mt-2 border-rose-500 text-rose-600 hover:bg-rose-50"
                  data-testid="backup-restore-btn"
                >
                  <UploadSimple size={18} weight="bold" className="mr-2" />
                  Restaurar respaldo
                </Button>
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium">🕒 Respaldos automáticos diarios</p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={refreshAutoBackups} disabled={autoBackupsLoading} data-testid="auto-backup-refresh">
                      <ArrowCounterClockwise size={14} className="mr-1" />
                      {autoBackupsLoading ? 'Cargando…' : 'Refrescar'}
                    </Button>
                    <Button size="sm" onClick={runBackupNow} disabled={runNowLoading} className="bg-violet-600 hover:bg-violet-700" data-testid="auto-backup-run-now">
                      {runNowLoading ? 'Generando…' : 'Crear ahora'}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  El servidor genera un respaldo cada 24h y conserva los últimos 7 archivos.
                  Te recomendamos <b>descargarlos a tu compu periódicamente</b> — el disco del servidor
                  podría reiniciarse al hacer un deploy.
                </p>
                {autoBackups.length === 0 ? (
                  <p className="text-xs text-slate-500 italic mt-2">
                    Aún no hay respaldos automáticos. Dale a "Crear ahora" para generar el primero.
                  </p>
                ) : (
                  <div className="mt-2 border rounded overflow-hidden">
                    <table className="w-full text-xs" data-testid="auto-backup-list">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left px-2 py-1">Archivo</th>
                          <th className="text-right px-2 py-1">Tamaño</th>
                          <th className="text-left px-2 py-1">Creado</th>
                          <th className="px-2 py-1"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {autoBackups.map((b) => (
                          <tr key={b.filename} className="border-t hover:bg-slate-50">
                            <td className="px-2 py-1 font-mono text-[11px] truncate max-w-[260px]">{b.filename}</td>
                            <td className="px-2 py-1 text-right">{(b.size_bytes / (1024 * 1024)).toFixed(2)} MB</td>
                            <td className="px-2 py-1">{new Date(b.created_at).toLocaleString('es-MX')}</td>
                            <td className="px-2 py-1 text-right flex gap-1 justify-end">
                              <Button size="sm" variant="ghost" onClick={() => downloadAutoBackup(b.filename)} data-testid={`auto-backup-download-${b.filename}`}>
                                <DownloadSimple size={14} />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => deleteAutoBackup(b.filename)} className="text-rose-600 hover:bg-rose-50" data-testid={`auto-backup-delete-${b.filename}`}>
                                ✕
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle>📑 Orden del menú</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" onClick={reset} data-testid="reset-menu">
                <ArrowCounterClockwise size={16} className="mr-1" /> Restablecer
              </Button>
              <Button onClick={save} disabled={saving} data-testid="save-menu" className="bg-violet-600 hover:bg-violet-700">
                <FloppyDisk size={16} className="mr-1" /> {saving ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground mb-3">
              Usa el handle <span className="font-mono">⋮⋮</span> para arrastrar, o las flechas. Los cambios se aplican para todos los usuarios.
            </p>
            <div className="space-y-1">
              {items.map((it, idx) => (
                <div
                  key={it.path}
                  onDragOver={onDragOver}
                  onDrop={(e) => onDrop(e, idx)}
                  className="flex items-center gap-2 border rounded p-2 bg-white hover:bg-slate-50"
                  data-testid={`menu-item-${it.path.slice(1)}`}
                >
                  <span
                    draggable
                    onDragStart={(e) => onDragStart(e, idx)}
                    className="text-slate-400 px-2 cursor-grab active:cursor-grabbing select-none"
                    title="Arrastra para reordenar"
                  >
                    ⋮⋮
                  </span>
                  <span className="text-sm font-mono text-muted-foreground w-8">{idx + 1}.</span>
                  <span className="flex-1 font-medium">{it.label}</span>
                  <code className="text-xs text-muted-foreground">{it.path}</code>
                  <Button variant="ghost" size="icon" onClick={() => move(idx, -1)} disabled={idx === 0}>
                    <ArrowUp size={16} />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => move(idx, 1)} disabled={idx === items.length - 1}>
                    <ArrowDown size={16} />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Diálogo: Restaurar respaldo */}
      <Dialog
        open={restoreDialog.open}
        onOpenChange={(o) => !o && !restoreDialog.restoring && setRestoreDialog({ open: false, file: null, confirm: '', restoring: false, result: null })}
      >
        <DialogContent className="max-w-lg" data-testid="restore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <ShieldWarning size={22} weight="fill" />
              Restaurar respaldo
            </DialogTitle>
          </DialogHeader>
          {restoreDialog.result ? (
            <div className="space-y-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm">
                <p className="font-semibold text-emerald-800">✅ Restauración completada</p>
                <p className="text-xs text-slate-700 mt-1">
                  Origen: <b>{restoreDialog.result.source_db}</b> · Exportado: {restoreDialog.result.exported_at?.slice(0,19)}
                </p>
              </div>
              <div className="max-h-60 overflow-y-auto border rounded text-xs">
                <table className="w-full">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1">Colección</th>
                      <th className="text-right px-2 py-1">Docs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(restoreDialog.result.restored || {}).sort().map(([k, v]) => (
                      <tr key={k} className="border-t">
                        <td className="px-2 py-1">{k}</td>
                        <td className="px-2 py-1 text-right font-semibold">{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {restoreDialog.result.errors?.length > 0 && (
                <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs">
                  <p className="font-semibold text-rose-800">{restoreDialog.result.errors.length} error(es):</p>
                  <ul className="list-disc pl-4 mt-1">
                    {restoreDialog.result.errors.map((e, i) => <li key={i}>{e.collection}: {e.error}</li>)}
                  </ul>
                </div>
              )}
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                🔄 Recarga la página para que los cambios surtan efecto en toda la app.
              </p>
              <div className="flex justify-end">
                <Button
                  onClick={() => { setRestoreDialog({ open: false, file: null, confirm: '', restoring: false, result: null }); window.location.reload(); }}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="restore-reload"
                >
                  Recargar página
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-rose-50 border border-rose-200 rounded p-3 text-xs text-rose-900">
                ⚠️ Esta acción <b>borra el contenido actual</b> de cada colección y lo reemplaza
                por el del archivo. No se puede deshacer (excepto restaurando otro respaldo).
                Asegúrate de que el archivo .json sea el correcto.
              </div>
              <div>
                <Label>Archivo .json</Label>
                <Input
                  type="file"
                  accept="application/json,.json"
                  onChange={(e) => setRestoreDialog(s => ({ ...s, file: e.target.files?.[0] || null }))}
                  data-testid="restore-file-input"
                />
                {restoreDialog.file && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {restoreDialog.file.name} · {(restoreDialog.file.size / 1024).toFixed(1)} KB
                  </p>
                )}
              </div>
              <div>
                <Label>Escribe <b className="text-rose-600">RESTAURAR</b> para confirmar</Label>
                <Input
                  value={restoreDialog.confirm}
                  onChange={(e) => setRestoreDialog(s => ({ ...s, confirm: e.target.value }))}
                  placeholder="RESTAURAR"
                  data-testid="restore-confirm-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="ghost"
                  onClick={() => setRestoreDialog({ open: false, file: null, confirm: '', restoring: false, result: null })}
                  disabled={restoreDialog.restoring}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={submitRestore}
                  disabled={!restoreDialog.file || restoreDialog.confirm !== 'RESTAURAR' || restoreDialog.restoring}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="restore-submit"
                >
                  {restoreDialog.restoring ? 'Restaurando…' : 'Restaurar ahora'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default Settings;
